'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { MdChevronLeft, MdChevronRight } from 'react-icons/md';
import { useTranslations } from 'next-intl';
import { cn } from '@/lib/styleUtils';
import { getYearPageStart, buildYearRange, YEARS_PER_PAGE } from './calendar-utils';
import {
  yearGridContainerClasses,
  yearGridClasses,
  yearCellClasses,
  yearNavClasses,
  yearNavButtonClasses,
} from './YearGrid.style';

export interface YearGridProps {
  /** The currently selected year. */
  selectedYear: number;
  /** The current calendar year (e.g., 2026), used for "current" highlight. */
  currentYear: number;
  /** Called when the user selects a year. */
  onSelect: (year: number) => void;
  /** Minimum selectable date (used to cap year navigation). */
  minDate?: Date;
  /** Maximum selectable date (used to cap year navigation). */
  maxDate?: Date;
}

/**
 * 4×4 year grid sub-component with 16-year pagination.
 * Shows 16 years per page with prev/next pagination arrows.
 * Full keyboard navigation: arrow keys, Enter/Space to select, PageUp/Down to paginate.
 */
export function YearGrid({ selectedYear, currentYear, onSelect, minDate, maxDate }: YearGridProps) {
  const t = useTranslations('DatePicker');
  const gridRef = useRef<HTMLDivElement>(null);
  const [pageStart, setPageStart] = useState(() => getYearPageStart(selectedYear));

  const years = buildYearRange(pageStart);
  const COLS = 4;
  const minYear = minDate ? minDate.getFullYear() : -Infinity;
  const maxYear = maxDate ? maxDate.getFullYear() : Infinity;
  const isYearDisabled = (year: number) => year < minYear || year > maxYear;

  const isPrevDisabled = pageStart - 1 < minYear;
  const isNextDisabled = pageStart + YEARS_PER_PAGE > maxYear;

  // Track whether pagination just happened to trigger focus restore.
  const didPaginateRef = useRef(false);

  const handlePrevPage = useCallback(() => {
    if (isPrevDisabled) return;
    didPaginateRef.current = true;
    setPageStart((prev) => prev - YEARS_PER_PAGE);
  }, [isPrevDisabled]);

  const handleNextPage = useCallback(() => {
    if (isNextDisabled) return;
    didPaginateRef.current = true;
    setPageStart((prev) => prev + YEARS_PER_PAGE);
  }, [isNextDisabled]);

  // After pagination, focus the first cell of the new page.
  useEffect(() => {
    if (!didPaginateRef.current) return;
    didPaginateRef.current = false;
    requestAnimationFrame(() => {
      gridRef.current?.querySelector<HTMLElement>('[data-year]')?.focus();
    });
  }, [pageStart]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const target = e.target as HTMLElement;
      const year = parseInt(target.getAttribute('data-year') ?? '', 10);
      if (isNaN(year)) return;

      const index = year - pageStart;
      let nextIndex: number | null = null;

      switch (e.key) {
        case 'ArrowLeft':
          nextIndex = index > 0 ? index - 1 : index;
          break;
        case 'ArrowRight':
          nextIndex = index < YEARS_PER_PAGE - 1 ? index + 1 : index;
          break;
        case 'ArrowUp':
          nextIndex = index - COLS >= 0 ? index - COLS : index;
          break;
        case 'ArrowDown':
          nextIndex = index + COLS < YEARS_PER_PAGE ? index + COLS : index;
          break;
        case 'Home':
          nextIndex = 0;
          break;
        case 'End':
          nextIndex = YEARS_PER_PAGE - 1;
          break;
        case 'PageUp':
          e.preventDefault();
          handlePrevPage();
          return;
        case 'PageDown':
          e.preventDefault();
          handleNextPage();
          return;
        case 'Enter':
        case ' ':
          e.preventDefault();
          if (!isYearDisabled(year)) onSelect(year);
          return;
        default:
          return;
      }

      e.preventDefault();
      if (nextIndex !== null) {
        const nextYear = pageStart + nextIndex;
        const cell = gridRef.current?.querySelector<HTMLElement>(`[data-year="${nextYear}"]`);
        cell?.focus();
      }
    },
    [pageStart, onSelect, handlePrevPage, handleNextPage]
  );

  const pageLabel = `${pageStart} – ${pageStart + YEARS_PER_PAGE - 1}`;

  return (
    <div className={yearGridContainerClasses}>
      <div
        ref={gridRef}
        className={yearGridClasses}
        role="grid"
        aria-label={t('selectYearShowing', { range: pageLabel })}
        onKeyDown={handleKeyDown}
      >
        {/* Render 4 rows × 4 cols */}
        {[0, 1, 2, 3].map((rowIdx) => (
          <div key={rowIdx} role="row" className="tw:contents">
            {[0, 1, 2, 3].map((colIdx) => {
              const yearIndex = rowIdx * COLS + colIdx;
              const year = years[yearIndex];
              const isSelected = year === selectedYear;
              const isCurrent = year === currentYear;

              const disabled = isYearDisabled(year);
              let state: 'default' | 'selected' | 'current' | 'disabled';
              if (disabled) {
                state = 'disabled';
              } else if (isSelected) {
                state = 'selected';
              } else if (isCurrent) {
                state = 'current';
              } else {
                state = 'default';
              }

              return (
                <button
                  key={year}
                  type="button"
                  role="gridcell"
                  aria-selected={isSelected}
                  aria-disabled={disabled || undefined}
                  aria-label={String(year)}
                  tabIndex={isSelected ? 0 : yearIndex === 0 ? 0 : -1}
                  data-year={year}
                  data-cy="calendar-year"
                  onClick={disabled ? undefined : () => onSelect(year)}
                  className={yearCellClasses({ state })}
                >
                  {year}
                </button>
              );
            })}
          </div>
        ))}
      </div>

      {/* Pagination footer */}
      <div className={yearNavClasses}>
        <button
          type="button"
          onClick={handlePrevPage}
          disabled={isPrevDisabled}
          className={cn(yearNavButtonClasses, isPrevDisabled && 'tw:opacity-30 tw:cursor-not-allowed')}
          aria-label={t('previousYears', { count: YEARS_PER_PAGE })}
          data-cy="calendar-year-prev"
        >
          <MdChevronLeft className="tw:h-5 tw:w-5" aria-hidden="true" />
        </button>
        <span className="tw:text-12 tw:text-font tw:font-medium">{pageLabel}</span>
        <button
          type="button"
          onClick={handleNextPage}
          disabled={isNextDisabled}
          className={cn(yearNavButtonClasses, isNextDisabled && 'tw:opacity-30 tw:cursor-not-allowed')}
          aria-label={t('nextYears', { count: YEARS_PER_PAGE })}
          data-cy="calendar-year-next"
        >
          <MdChevronRight className="tw:h-5 tw:w-5" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
